import { http } from 'app/http';

export interface NDATemplateInfo {
  id: string;
  name: string;
  type: 'bilateral' | 'unilateral';
  description?: string;
}
export interface GenerateNDARequest {
  ndaType?: 'bilateral' | 'unilateral';
  xCompany?: string;
  xAddress?: string;
  xMail?: string;
  zurichAddress?: string;
  zurichMail?: string;
  project?: string;
  date?: string;
  xSigner?: string;
  zurichSigner?: string;
  showDraftingNote?: boolean;
  templateId?: string;
  disclosing_party?: string;
  receiving_party?: string;
  effective_date?: string;
  term_months?: number;
  jurisdiction?: string;
  purpose?: string;
  confidentiality_scope?: string[];
  confidentiality_scope_text?: string;
  exclusions?: string[];
  exclusions_text?: string;
  permitted_disclosures?: string[];
  project_description?: string;
  counterparty_address?: string | null;
  counterparty_email?: string | null;
  counterparty_signer?: string | null;
  zurich_address?: string | null;
  zurich_email?: string | null;
  zurich_signer?: string | null;
  disclosure_nature?: 'bilateral' | 'unilateral';
  language?: string;
  survival_period_months?: number | null;
  template_id?: string;
}
export interface GenerateNDAResponse {
  documentId: string;
  fileName?: string;

  docxBase64?: string;
  docxFilename?: string;

  document?: string;
}
export interface AnalyzeNDAResponse {
  comparisonId: string;
  report?: any;
  sideBySideComparison?: any;
  comparisonResultFiles?: Record<string, string>;
}

export interface AnalyzeAsyncRequest {
  customerId: string;
  zurichId?: string;
}
export interface AnalyzeTextRequest {
  customerText: string;
  zurichId?: string;
}

const BASE = 'legals/nda-generator';

export async function getTemplates(): Promise<NDATemplateInfo[]> {
  const res = await http.get<NDATemplateInfo[]>(`${BASE}/templates`);
  return res.data;
}

export async function generateNDA(
  payload: GenerateNDARequest
): Promise<GenerateNDAResponse> {
  const res = await http.post<GenerateNDAResponse>(`${BASE}`, payload);
  return res.data;
}

export async function downloadNDA(
  documentId: string,
  token?: string
): Promise<Blob> {
  const url = token
    ? `${BASE}/documents/${documentId}/download?token=${encodeURIComponent(
        token
      )}`
    : `${BASE}/documents/${documentId}/download`;
  const res = await http.get<Blob>(url, {
    responseType: 'blob'
  });
  return res.data;
}

export async function uploadCustomer(
  file: File
): Promise<{ documentId: string; customerText?: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await http.post<{
    documentId?: string;
    customerId?: string;
    customerText?: string;
  }>(`${BASE}/review/upload-customer`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  const rawId = res.data.documentId ?? res.data.customerId;
  const id = (rawId ?? '').trim();
  if (!id) {
    throw new Error(
      `Upload customer response missing documentId/customerId. Raw: ${JSON.stringify(
        res.data
      )}`
    );
  }
  return { documentId: id, customerText: res.data.customerText };
}

export async function uploadZurich(
  file: File
): Promise<{ documentId: string; zurichText?: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await http.post<{
    documentId?: string;
    zurichId?: string;
    zurichText?: string;
  }>(`${BASE}/review/upload-zurich`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  const rawId = res.data.documentId ?? res.data.zurichId;
  const id = (rawId ?? '').trim();
  if (!id) {
    throw new Error(
      `Upload Zurich response missing documentId/zurichId. Raw: ${JSON.stringify(
        res.data
      )}`
    );
  }
  return { documentId: id, zurichText: res.data.zurichText };
}

export async function analyzeAsync(
  payload: AnalyzeAsyncRequest
): Promise<AnalyzeNDAResponse> {
  const res = await http.post<AnalyzeNDAResponse>(
    `${BASE}/review/analyze-async`,
    payload,
    { timeout: 60000 }
  );
  return res.data;
}

export async function analyzeCompliance(
  payload: AnalyzeAsyncRequest
): Promise<AnalyzeNDAResponse> {
  return await postAnalyze(payload);
}

export async function analyzeComplianceText(
  payload: AnalyzeTextRequest
): Promise<AnalyzeNDAResponse> {
  return await postAnalyze(payload);
}

async function postAnalyze(
  payload: AnalyzeAsyncRequest | AnalyzeTextRequest
): Promise<AnalyzeNDAResponse> {
  const res = await http.post<AnalyzeNDAResponse>(
    `${BASE}/review/analyze`,
    payload,
    { timeout: 60000 }
  );
  return res.data;
}

export const ndaApi = {
  getTemplates,
  generateNDA,
  downloadNDA,
  uploadCustomer,
  uploadZurich,
  analyzeAsync,
  analyzeCompliance,
  analyzeComplianceText
};
